// Requests from a warehouse by quantity (CR_20260915_Request_Qty R2): reads of request_overview (RLS decides who sees
// what), availability (managers and warehouse staff only — the server refuses a project user), the menu chip, and
// the RPCs, each with an idempotency key like the transfer commands.
import type {
  CoverReason,
  ProjectApproval,
  RequestAvailability,
  RequestOverview,
  RequestPriority,
} from '@fleetspoke/core/requests';
import type { LineStatus, TransferStatus } from '@fleetspoke/core/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type { CoverReason, ProjectApproval };
import { supabase } from '../lib/supabase';

async function data<T>(
  q: PromiseLike<{
    data: unknown;
    error: { message: string; code?: string; details?: string | null; hint?: string | null } | null;
  }>,
): Promise<T> {
  const { data: d, error } = await q;
  // details + hint carry the dispatch refusal's blockers (CR_20260921 L6 — @fleetspoke/core/lifecycle)
  if (error)
    throw Object.assign(new Error(error.message), {
      code: error.code,
      details: error.details,
      hint: error.hint,
    });
  return d as T;
}

export const requestKeys = {
  all: ['requests'] as const,
  one: (id: string) => ['requests', 'one', id] as const,
  transfers: (id: string) => ['requests', 'transfers', id] as const,
  /** under 'requests', so every request command refreshes the chip */
  count: ['requests', 'count'] as const,
  availability: (categories: string[]) => ['requests', 'availability', categories] as const,
  approvers: (projectId: string) => ['requests', 'approvers', projectId] as const,
  approval: ['requests', 'approval'] as const,
  /** per-project overrides + the cover share, for Settings → Approvals (CR_20260919) */
  projectApproval: ['requests', 'project-approval'] as const,
  coverStats: ['requests', 'cover-stats'] as const,
};

export function useRequests(enabled = true) {
  return useQuery({
    queryKey: requestKeys.all,
    enabled,
    queryFn: () =>
      data<RequestOverview[]>(
        supabase()
          .from('request_overview')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(500),
      ),
  });
}

export function useRequest(id: string) {
  return useQuery({
    queryKey: requestKeys.one(id),
    enabled: !!id,
    queryFn: () =>
      data<RequestOverview | null>(
        supabase().from('request_overview').select('*').eq('request_id', id).maybeSingle(),
      ),
  });
}

export interface RequestTransfer {
  id: string;
  ref: string;
  status: TransferStatus;
  from_site_id: string | null;
  to_site_id: string | null;
  created_by: string | null;
  vehicle_ref: string | null;
  note: string | null;
  dispatched_at: string | null;
  received_at: string | null;
  received_by: string | null;
  transfer_lines: { asset_id: string; status: LineStatus; assets: { asset_code: string } | null }[];
}

/** The transfers made from a request (what the viewer may see of them). */
export function useRequestTransfers(id: string) {
  return useQuery({
    queryKey: requestKeys.transfers(id),
    enabled: !!id,
    queryFn: () =>
      data<RequestTransfer[]>(
        supabase()
          .from('transfers')
          .select(
            'id, ref, status, from_site_id, to_site_id, created_by, vehicle_ref, note, dispatched_at, received_at, received_by, transfer_lines(asset_id, status, assets(asset_code))',
          )
          .eq('request_id', id)
          .order('created_at'),
      ),
  });
}

/** Per warehouse × category (managers, warehouse staff). Never called for a project user. */
export function useRequestAvailability(categories: string[], enabled: boolean) {
  const ids = [...categories].sort();
  return useQuery({
    queryKey: requestKeys.availability(ids),
    enabled: enabled && ids.length > 0,
    queryFn: () =>
      data<RequestAvailability[]>(supabase().rpc('request_availability', { p_categories: ids })),
  });
}

/** The Requests menu chip (Q16): how many wait for the viewer, and how many of those are urgent or late. */
export function useRequestActionCount(enabled: boolean) {
  return useQuery({
    queryKey: requestKeys.count,
    enabled,
    queryFn: async () => {
      const rows = await data<{ waiting: number; hot: number }[]>(
        supabase().rpc('request_action_count'),
      );
      return rows[0] ?? { waiting: 0, hot: 0 };
    },
    refetchInterval: 60_000,
  });
}

export function useProjectApprovers(projectId: string, enabled = true) {
  return useQuery({
    queryKey: requestKeys.approvers(projectId),
    enabled: enabled && !!projectId,
    queryFn: async () =>
      (
        await data<{ user_id: string }[]>(
          supabase().from('project_approvers').select('user_id').eq('project_id', projectId),
        )
      ).map((r) => r.user_id),
  });
}

/** Active managers of the account, by name (project approvers are chosen from them). */
export function useCompanyManagers(enabled = true) {
  return useQuery({
    queryKey: ['requests', 'managers'],
    enabled,
    queryFn: async () => {
      const [m, p] = await Promise.all([
        supabase()
          .from('memberships')
          .select('user_id')
          .eq('is_active', true)
          .eq('role', 'manager'),
        supabase().from('profiles').select('user_id, full_name, phone'),
      ]);
      const failed = m.error ?? p.error;
      if (failed) throw new Error(failed.message);
      const names = new Map((p.data ?? []).map((x) => [x.user_id, x.full_name || x.phone || '—']));
      return (m.data ?? [])
        .map((x) => ({ user_id: x.user_id, name: names.get(x.user_id) ?? '—' }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

/** The company's "Approval required" switch (companies.settings.request_approval; on unless 'none'). */
export function useRequestApprovalRequired(companyId: string | undefined) {
  return useQuery({
    queryKey: requestKeys.approval,
    enabled: !!companyId,
    queryFn: async () => {
      const row = await data<{ settings: Record<string, unknown> | null } | null>(
        supabase().from('companies').select('settings').eq('id', companyId!).maybeSingle(),
      );
      return row?.settings?.request_approval !== 'none';
    },
  });
}

/**
 * Is the signed-in person one of this project's named approvers? True when none are named — then any manager
 * decides, as before. Mirrors `app.is_request_approver`, which is what actually enforces it (D2).
 */
export function useIsRequestApprover(projectId: string, userId: string | undefined) {
  const approvers = useProjectApprovers(projectId, !!projectId);
  return {
    // while loading, assume approver: the server refuses anyway, and a cover form that flashes up is worse
    isApprover:
      !approvers.data || approvers.data.length === 0 || approvers.data.includes(userId ?? ''),
    loading: approvers.isLoading,
  };
}

/** Every project's approval rule, for the Settings → Approvals table. RLS scopes it to the account. */
export function useProjectApprovals(enabled = true) {
  return useQuery({
    queryKey: requestKeys.projectApproval,
    enabled,
    queryFn: async () =>
      new Map(
        (
          await data<{ id: string; approval: ProjectApproval }[]>(
            supabase().from('projects').select('id, approval'),
          )
        ).map((r) => [r.id, r.approval]),
      ),
  });
}

/**
 * How many of each project's recent approvals were somebody covering (D6 safeguard 2). Read it as feedback on the
 * approver list, not a number to drive down: a project whose named approvers rarely approve has the wrong names.
 */
export function useApprovalCoverStats(days = 30, enabled = true) {
  return useQuery({
    queryKey: [...requestKeys.coverStats, days],
    enabled,
    queryFn: async () =>
      new Map(
        (
          await data<{ project_id: string; approvals: number; cover: number }[]>(
            supabase().rpc('approval_cover_stats', { p_days: days }),
          )
        ).map((r) => [r.project_id, { approvals: r.approvals, cover: r.cover }]),
      ),
  });
}

// ------------------------------------------------------------------------------------------------ commands
const rpc = <T>(fn: string, args: Record<string, unknown>) =>
  data<T>(supabase().rpc(fn, { p_idempotency_key: crypto.randomUUID(), ...args }));

/** A request command: refreshes requests (and the chip), transfers, boards and the dashboard. */
function useRequestCommand<A, R = unknown>(fn: (args: A) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of [
        requestKeys.all,
        ['web-transfers'],
        ['board'],
        ['sites'],
        ['needs-action'],
      ])
        void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export interface NewRequest {
  toSiteId: string;
  lines: { categoryId: string; qty: number; spec: string }[];
  neededBy: string;
  priority: RequestPriority;
  requestedFor: string;
  note: string;
  preferredSourceId: string | null;
}

export const useSubmitRequest = () =>
  useRequestCommand((a: NewRequest) =>
    rpc<{ request_id: string; ref: string; status: string }>('submit_request', {
      p_to_site_id: a.toSiteId,
      p_lines: a.lines.map((l) => ({
        category_id: l.categoryId,
        qty: l.qty,
        spec: l.spec.trim() || null,
      })),
      p_needed_by: a.neededBy,
      p_priority: a.priority,
      p_requested_for: a.requestedFor.trim() || null,
      p_note: a.note.trim() || null,
      p_preferred_source_site_id: a.preferredSourceId,
    }),
  );

export const useDecideRequest = () =>
  useRequestCommand(
    (a: {
      requestId: string;
      approve: boolean;
      sourceId?: string | null;
      lines?: { categoryId: string; qty: number }[];
      note: string;
      /** required when the decider is not one of the project's named approvers (D2/D6) */
      coverReason?: CoverReason | null;
    }) =>
      rpc('decide_request', {
        p_request_id: a.requestId,
        p_approve: a.approve,
        p_source_site_id: a.sourceId ?? null,
        p_lines: a.lines ? a.lines.map((l) => ({ category_id: l.categoryId, qty: l.qty })) : null,
        p_note: a.note.trim() || null,
        p_cover_reason: a.coverReason ?? null,
      }),
  );

/** Writes a project's approval rule; null follows the company default. */
export const useSetProjectApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { projectId: string; approval: ProjectApproval }) =>
      data<string | null>(
        supabase().rpc('set_project_approval', {
          p_project_id: a.projectId,
          p_approval: a.approval,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: requestKeys.projectApproval });
      void queryClient.invalidateQueries({ queryKey: requestKeys.all });
    },
  });
};

export const useCancelRequest = () =>
  useRequestCommand((a: { requestId: string; note: string }) =>
    rpc('cancel_request', { p_request_id: a.requestId, p_note: a.note.trim() || null }),
  );

export const useFulfilRequest = () =>
  useRequestCommand(
    (a: {
      requestId: string;
      assetIds: string[];
      vehicleRef: string;
      note: string;
      /** a manager's reason for dispatching past a certificate block (CR_20260921 L6a) */
      overrideReason?: string | null;
    }) =>
      rpc<{ transfer_id: string; ref: string; override?: boolean }>('fulfil_request', {
        p_request_id: a.requestId,
        p_asset_ids: a.assetIds,
        p_vehicle_ref: a.vehicleRef.trim() || null,
        p_note: a.note.trim() || null,
        ...(a.overrideReason ? { p_override_reason: a.overrideReason } : {}),
      }),
  );

export const useCloseRequestShort = () =>
  useRequestCommand((a: { requestId: string; note: string }) =>
    rpc('close_request_short', { p_request_id: a.requestId, p_note: a.note.trim() }),
  );

export const useReturnRequest = () =>
  useRequestCommand((a: { requestId: string; note: string }) =>
    rpc('return_request_to_manager', { p_request_id: a.requestId, p_note: a.note.trim() }),
  );

export function useSetProjectApprovers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { projectId: string; userIds: string[] }) =>
      data<number>(
        supabase().rpc('set_project_approvers', {
          p_project_id: a.projectId,
          p_user_ids: a.userIds,
        }),
      ),
    onSuccess: (_, a) =>
      void queryClient.invalidateQueries({ queryKey: requestKeys.approvers(a.projectId) }),
  });
}

export function useSetRequestApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (required: boolean) =>
      data<boolean>(supabase().rpc('set_request_approval', { p_required: required })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: requestKeys.approval }),
  });
}

/** A request error as a sentence: not allowed, invalid input, or no longer in that state. */
export function requestError(e: unknown, t: (k: string) => string): string {
  const code = (e as { code?: string } | null)?.code;
  if (code === '42501') return t('common.not_allowed');
  if (code === '55000') return t('requests.err_state');
  return (e as Error)?.message ?? t('common.error_generic');
}
